export type BrandFamilyLocale = 'de' | 'ro' | 'en' | 'fr' | 'es' | 'pt' | 'zh' | 'hi' | 'ar' | 'bn';

export interface WhyCryptoReason {
  title?: string;
  text?: string;
}

export interface FaqItem {
  question?: string;
  answer?: string;
}

export interface BrandFamilySections {
  whatIs?: string;
  howToBuySteps?: string[];
  whyCryptoReasons?: WhyCryptoReason[];
  faq?: FaqItem[];
}

export interface BrandFamilyI18nFields {
  displayName?: string;
  description?: string;
  h1?: string;
  metaTitle?: string;
  metaDescription?: string;
}

export interface BrandFamilyI18nLocale {
  u?: string;
  fields?: BrandFamilyI18nFields;
  sections?: BrandFamilySections;
}

export type BrandFamilyI18n = Partial<Record<BrandFamilyLocale, BrandFamilyI18nLocale>>;

export interface BrandFamily {
  _id: string;
  slug: string;
  displayName: string;
  logo?: string;
  description?: string;
  sections?: BrandFamilySections;
  metaTitle?: string;
  metaDescription?: string;
  h1?: string;
  isEnabled: boolean;
  s: number;
  relatedBrandIds?: string[];
  sideCtaCardIds?: string[];
  i18n?: BrandFamilyI18n;
  createdAt?: string;
  updatedAt?: string;
}

export interface BrandFamilyWithProducts extends BrandFamily {
  productCount?: number;
}

/**
 * Slim projection for brand card grids rendered by client components - full
 * BrandFamily carries per-locale sections/descriptions that must not ride
 * the RSC hydration payload (ACEB-162). Same pattern as CardGridItem.
 */
export type BrandCardItem = Pick<BrandFamily, '_id' | 'slug' | 'displayName' | 'logo' | 'i18n'>;

export function toBrandCardItem(brand: BrandFamily, locale?: BrandFamilyLocale): BrandCardItem {
  const localizedName = locale ? brand.i18n?.[locale]?.fields?.displayName : undefined;
  return {
    _id: brand._id,
    slug: brand.slug,
    displayName: brand.displayName,
    logo: brand.logo,
    ...(localizedName !== undefined && {
      i18n: { [locale as BrandFamilyLocale]: { fields: { displayName: localizedName } } } as BrandFamilyI18n,
    }),
  };
}

function getI18nField(brand: Pick<BrandFamily, 'i18n'> | undefined, locale: BrandFamilyLocale | undefined, field: keyof BrandFamilyI18nFields): string | undefined {
  if (!brand || !locale || !brand.i18n) return undefined;
  return brand.i18n[locale]?.fields?.[field];
}

export function getBrandDisplayName(brand: Pick<BrandFamily, 'displayName' | 'i18n'> | undefined, locale?: BrandFamilyLocale): string {
  if (!brand) return '';
  return getI18nField(brand, locale, 'displayName') || brand.displayName;
}

export function getBrandDescription(brand: BrandFamily | undefined, locale?: BrandFamilyLocale): string | undefined {
  if (!brand) return undefined;
  return getI18nField(brand, locale, 'description') || brand.description;
}

export function getBrandSections(brand: BrandFamily | undefined, locale?: BrandFamilyLocale): BrandFamilySections | undefined {
  if (!brand) return undefined;
  const i18nSections = locale && brand.i18n?.[locale]?.sections;
  if (!i18nSections) return brand.sections;
  return {
    whatIs: i18nSections.whatIs || brand.sections?.whatIs,
    howToBuySteps: i18nSections.howToBuySteps?.length ? i18nSections.howToBuySteps : brand.sections?.howToBuySteps,
    whyCryptoReasons: i18nSections.whyCryptoReasons?.length ? i18nSections.whyCryptoReasons : brand.sections?.whyCryptoReasons,
    faq: i18nSections.faq?.length ? i18nSections.faq : brand.sections?.faq,
  };
}

export function getBrandH1(brand: BrandFamily | undefined, locale?: BrandFamilyLocale): string | undefined {
  if (!brand) return undefined;
  return getI18nField(brand, locale, 'h1') || brand.h1;
}

export function getBrandMetaTitle(brand: BrandFamily | undefined, locale?: BrandFamilyLocale): string | undefined {
  if (!brand) return undefined;
  return getI18nField(brand, locale, 'metaTitle') || brand.metaTitle;
}

export function getBrandMetaDescription(brand: BrandFamily | undefined, locale?: BrandFamilyLocale): string | undefined {
  if (!brand) return undefined;
  return getI18nField(brand, locale, 'metaDescription') || brand.metaDescription;
}
